<ng-container *transloco="let t; read: 'providers.feedFiles'">
  <app-modal
    [(open)]="open"
    [title]="isCreate() ? t('dialog.createTitle') : t('dialog.editTitle', { filename: filename() })"
    size="medium"
    [primaryLabel]="isSaving() ? t('dialog.saving') : isCreate() ? t('dialog.create') : t('dialog.save')"
    [primaryDisabled]="!isValid() || isSaving()"
    (primaryAction)="save()"
    (closed)="close()"
  >
    <div class="flex flex-col gap-5">
      @if (isCreate()) {
        <app-form-field [help]="t('dialog.filenameHelp')" [error]="fieldError('filename')">
          <app-text-input
            [label]="t('dialog.filename')"
            [ngModel]="filename()"
            (ngModelChange)="filename.set($event)"
            [ngModelOptions]="{ standalone: true }"
            [placeholder]="t('dialog.filenamePlaceholder')"
          ></app-text-input>
        </app-form-field>
      }

      @if (rolesDeclared()) {
        <!-- The provider declares its roles: only those can be chosen (core rejects others). -->
        <app-form-field [help]="t('dialog.roleHelpDeclared')" [error]="fieldError('role')">
          <app-select
            [label]="t('dialog.role') + ' *'"
            [options]="roleOptions()"
            [invalid]="!!fieldError('role')"
            [ngModel]="role() || null"
            (ngModelChange)="role.set($event ?? '')"
            [ngModelOptions]="{ standalone: true }"
          ></app-select>
        </app-form-field>
      } @else {
        <app-form-field [help]="t('dialog.roleHelp')">
          <app-text-input
            [label]="t('dialog.role')"
            [ngModel]="role()"
            (ngModelChange)="role.set($event)"
            [ngModelOptions]="{ standalone: true }"
            [placeholder]="t('dialog.optional')"
          ></app-text-input>
        </app-form-field>
      }

      <!-- Source -->
      <div class="flex flex-col gap-2">
        <span class="px-2 text-[12px] font-semibold text-ink-2">{{ t('dialog.source') }}</span>
        <app-segmented-control
          [segments]="sourceSegments()"
          [ngModel]="sourceType()"
          (ngModelChange)="setSource($event)"
          [ngModelOptions]="{ standalone: true }"
        />
        <span class="px-2 text-[12px] leading-4 text-ink-3">{{ t('dialog.sourceHelp.' + sourceType()) }}</span>
      </div>

      @if (sourceType() === 'MANUAL') {
        @if (isCreate()) {
          <app-form-field [help]="t('dialog.contentHelp', { max: formatBytes(maxBytes) })" [error]="fieldError('content')">
            <div class="flex flex-wrap items-center gap-3">
              <label
                class="flex h-8 cursor-pointer items-center rounded-control px-3 text-[13px] font-semibold text-ink-1 shadow-hairline hover:bg-sunken"
              >
                {{ t('dialog.chooseContent') }}
                <input type="file" class="sr-only" [attr.accept]="pickerAccept() || null" (change)="onContentPicked($event)" />
              </label>
              <span class="min-w-0 truncate text-[13px]" [class.text-ink-1]="contentFile()" [class.text-ink-3]="!contentFile()">
                {{ contentFile()?.name ?? t('dialog.noContentChosen') }}
              </span>
            </div>
          </app-form-field>
        } @else if (file()?.sourceConfig?.URL) {
          <p class="m-0 rounded-control bg-sunken px-3 py-2 text-[12px] leading-[18px] text-ink-2">{{ t('dialog.urlKept') }}</p>
        }
      } @else {
        <!-- URL source -->
        <app-form-field [error]="fieldError('url')">
          <app-text-input
            [label]="t('dialog.url')"
            [ngModel]="url()"
            (ngModelChange)="url.set($event)"
            [ngModelOptions]="{ standalone: true }"
            [placeholder]="t('dialog.urlPlaceholder')"
          ></app-text-input>
        </app-form-field>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <app-select
            [label]="t('dialog.interval')"
            [options]="intervalOptions()"
            [ngModel]="interval()"
            (ngModelChange)="interval.set($event)"
            [ngModelOptions]="{ standalone: true }"
          ></app-select>
          <app-select
            [label]="t('dialog.authType')"
            [options]="authOptions()"
            [ngModel]="authType()"
            (ngModelChange)="setAuthType($event)"
            [ngModelOptions]="{ standalone: true }"
          ></app-select>
        </div>

        @switch (authType()) {
          @case ('BASIC') {
            <app-form-field [error]="fieldError('username')">
              <app-text-input
                [label]="t('dialog.username')"
                [ngModel]="username()"
                (ngModelChange)="username.set($event)"
                [ngModelOptions]="{ standalone: true }"
              ></app-text-input>
            </app-form-field>
          }
          @case ('HEADER') {
            <app-form-field [error]="fieldError('headerName')">
              <app-text-input
                [label]="t('dialog.headerName')"
                [ngModel]="headerName()"
                (ngModelChange)="headerName.set($event)"
                [ngModelOptions]="{ standalone: true }"
                [placeholder]="t('dialog.headerNamePlaceholder')"
              ></app-text-input>
            </app-form-field>
          }
          @case ('QUERY') {
            <app-form-field [error]="fieldError('paramName')">
              <app-text-input
                [label]="t('dialog.paramName')"
                [ngModel]="paramName()"
                (ngModelChange)="paramName.set($event)"
                [ngModelOptions]="{ standalone: true }"
                [placeholder]="t('dialog.paramNamePlaceholder')"
              ></app-text-input>
            </app-form-field>
          }
        }

        @if (needsSecret()) {
          @if (secretEditable()) {
            <app-form-field [help]="t('dialog.secretHelp')" [error]="fieldError('secret')">
              <app-text-input
                type="password"
                [label]="t('dialog.secret.' + authType())"
                [ngModel]="secret()"
                (ngModelChange)="secret.set($event)"
                [ngModelOptions]="{ standalone: true }"
              ></app-text-input>
            </app-form-field>
            @if (secretStored()) {
              <button
                type="button"
                (click)="replacingSecret.set(false); secret.set('')"
                class="w-fit px-2 text-[13px] font-semibold text-link hover:underline"
              >
                {{ t('dialog.keepSecret') }}
              </button>
            }
          } @else {
            <div class="flex items-center justify-between gap-3 rounded-control bg-sunken px-3 py-2.5 text-[13px]">
              <span class="text-ink-2">{{ t('dialog.secretSet.' + authType()) }}</span>
              <button type="button" (click)="replacingSecret.set(true)" class="font-semibold text-link hover:underline">
                {{ t('dialog.replaceSecret') }}
              </button>
            </div>
          }
        }
      }

      <!-- Metadata -->
      <div class="flex flex-col gap-2">
        <span class="px-2 text-[12px] font-semibold text-ink-2">{{ t('dialog.metadata') }}</span>
        @for (row of metadataRows(); track $index; let i = $index) {
          <div class="grid grid-cols-[180px_minmax(0,1fr)_24px] items-center gap-3">
            <app-text-input
              size="sm"
              [ngModel]="row.key"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="setMetadata(i, 'key', $event)"
              [placeholder]="t('upload.metadataKey')"
            ></app-text-input>
            <app-text-input
              size="sm"
              [ngModel]="row.value"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="setMetadata(i, 'value', $event)"
              [placeholder]="t('upload.metadataValue')"
            ></app-text-input>
            <button type="button" (click)="removeMetadataRow(i)" [attr.aria-label]="t('upload.remove')" class="text-ink-3 hover:text-danger">&times;</button>
          </div>
        }
        <button type="button" (click)="addMetadataRow()" class="w-fit px-2 text-[13px] font-semibold text-link hover:underline">
          {{ t('upload.addMetadata') }}
        </button>
        @if (fieldError('metadata'); as err) {
          <span class="px-2 text-[12px] font-semibold text-danger">{{ err }}</span>
        }
      </div>

      @if (error(); as message) {
        <p class="m-0 rounded-control bg-danger-surface px-3 py-2 text-[13px] leading-5 text-danger">{{ message }}</p>
      }
    </div>
  </app-modal>
</ng-container>
